/**
 * Monkey-patches the global `fetch` and `XMLHttpRequest` so that every
 * outbound API call:
 *   1. carries a fresh W3C `traceparent` header (so backend spans continue
 *      the trace),
 *   2. is wrapped in a real OTel CLIENT span on the browser side, exported
 *      via the OTLP/HTTP receiver (gateway → alloy → tempo) so Tempo has
 *      a visible root for the trace,
 *   3. carries page-context headers (`x-clip-page-url`, `x-clip-referer`)
 *      that Envoy stamps onto the gateway span as customTags — redundant
 *      with the browser span, but a useful safety net for traces where
 *      the browser exporter dropped a batch.
 *
 * Both fetch and XHR are patched because in-tree code uses fetch directly
 * in some places and axios in others — axios defaults to XHR in the
 * browser. Patching one but not the other leaves a silent gap.
 *
 * Idempotent: a marker on the patched function prevents double-wrapping
 * if `initObservability` runs twice (HMR, tests, etc.).
 */

import { newTraceparent, shouldPropagateTo, asUrlString } from './browserTracing';
import { snapshotBreadcrumbs } from './actionTracker';
import { encodeBreadcrumbs, recordBrowserSpan } from './otlpExporter';
import { getUserContext } from './userContext';

const PATCH_MARKER = '__clipsymphonyTracePatched';

interface PatchableFunction {
  [PATCH_MARKER]?: boolean;
}

export interface PropagationContext {
  propagationTargets: Array<RegExp | string>;
  /** Used as the OTel resource service.name for browser spans. */
  serviceName: string;
}

const SPAN_KIND_CLIENT = 3;

function pathnameOf(url: string): string {
  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.href : undefined;
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
}

function pageContextAttrs(): Record<string, string | boolean | undefined> {
  const user = getUserContext();
  const attrs: Record<string, string | boolean | undefined> = {
    'clip.route': user.route,
    'clip.page.title': user.pageTitle,
    'clip.user.id': user.userId,
    'clip.user.email': user.email,
    'clip.user.name': user.name,
    'clip.user.scopes': user.scopes,
    'clip.user.tenant': user.tenant,
    'clip.user.token_expired': user.expired,
    'clip.user.token_issuer': user.issuer,
    'clip.user.token_id': user.tokenId,
    'clip.session.anonymous_id': user.anonymousId,
    'clip.browser.viewport': user.viewport,
    'clip.browser.language': user.language,
    'clip.browser.i18n_language': user.i18nLanguage,
    'clip.browser.timezone': user.timezone,
    'clip.browser.color_scheme': user.colorScheme,
    'clip.browser.connection_type': user.connectionType,
    'clip.browser.online': user.online,
  };
  if (typeof window !== 'undefined' && window.location) {
    attrs['page.url'] = window.location.href;
  }
  if (typeof window !== 'undefined' && window.document?.referrer) {
    attrs['page.referrer'] = window.document.referrer;
  }
  return attrs;
}

function isSameOrigin(targetUrl: string): boolean {
  if (typeof window === 'undefined' || !window.location) return true;
  if (targetUrl.startsWith('/') && !targetUrl.startsWith('//')) return true;
  try {
    return new URL(targetUrl, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Custom `x-clip-*` headers exist so Envoy can stamp them as span customTags
 * on the gateway-side span. They are useless on requests that don't hit our
 * gateway, AND they break CORS preflights against any backend whose
 * Access-Control-Allow-Headers list doesn't include them (e.g. the auth
 * service rejected `x-clip-page-url` and broke the login POST). So only
 * attach them when the target is same-origin — our backends behind the
 * gateway always are, third-party endpoints are not. The span's own
 * attributes carry the same context regardless.
 */
function pageContextHeaders(targetUrl: string): Record<string, string> {
  if (!isSameOrigin(targetUrl)) return {};
  const out: Record<string, string> = {};
  if (typeof window !== 'undefined' && window.location) {
    out['x-clip-page-url'] = window.location.href;
    out['x-clip-route'] = window.location.pathname + window.location.search;
  }
  if (typeof window !== 'undefined' && window.document?.referrer) {
    out['x-clip-referer'] = window.document.referrer;
  }
  const user = getUserContext();
  if (user.userId) out['x-clip-user-id'] = user.userId;
  if (user.email) out['x-clip-user-email'] = user.email;
  if (user.name) out['x-clip-user-name'] = user.name;
  if (user.scopes) out['x-clip-user-scopes'] = user.scopes;
  if (user.tenant) out['x-clip-user-tenant'] = user.tenant;
  if (user.anonymousId) out['x-clip-anonymous-id'] = user.anonymousId;
  return out;
}

export function installGlobalTracePropagation(ctx: PropagationContext): void {
  patchFetch(ctx);
  patchXhr(ctx);
}

function patchFetch(ctx: PropagationContext): void {
  if (typeof globalThis === 'undefined' || typeof globalThis.fetch !== 'function') {
    return;
  }

  const original = globalThis.fetch as typeof fetch & PatchableFunction;
  if (original[PATCH_MARKER]) {
    return;
  }

  const wrapped: typeof fetch & PatchableFunction = ((
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const targetUrl = asUrlString(input);

    if (!shouldPropagateTo(targetUrl, ctx.propagationTargets)) {
      return original(input, init);
    }

    const headers = new Headers(init?.headers);

    // Respect an upstream-provided traceparent (e.g. worker forwarding a
    // trace from another context).
    let traceId: string;
    let spanId: string;
    const existing = headers.get('traceparent');
    if (existing) {
      const parts = existing.split('-');
      traceId = parts[1] ?? newTraceparent().traceId;
      spanId = parts[2] ?? newTraceparent().spanId;
    } else {
      const tp = newTraceparent();
      headers.set('traceparent', tp.header);
      traceId = tp.traceId;
      spanId = tp.spanId;
    }

    for (const [k, v] of Object.entries(pageContextHeaders(targetUrl))) {
      if (!headers.has(k)) headers.set(k, v);
    }

    const method = (init?.method || 'GET').toUpperCase();
    const startTimeMs = Date.now();
    const breadcrumbs = encodeBreadcrumbs(snapshotBreadcrumbs());

    const finalInit: RequestInit = { ...(init ?? {}), headers };

    const promise = original(input, finalInit);
    promise.then(
      (res) => {
        recordBrowserSpan({
          traceId,
          spanId,
          name: `${method} ${pathnameOf(targetUrl)}`,
          kind: SPAN_KIND_CLIENT,
          startTimeMs,
          endTimeMs: Date.now(),
          attributes: {
            'http.method': method,
            'http.url': targetUrl,
            'http.status_code': res.status,
            'clip.recent_actions': breadcrumbs,
            ...pageContextAttrs(),
          },
          statusCode: res.ok ? 1 : 2,
          statusMessage: res.ok ? undefined : `HTTP ${res.status}`,
        });
      },
      (err) => {
        recordBrowserSpan({
          traceId,
          spanId,
          name: `${method} ${pathnameOf(targetUrl)}`,
          kind: SPAN_KIND_CLIENT,
          startTimeMs,
          endTimeMs: Date.now(),
          attributes: {
            'http.method': method,
            'http.url': targetUrl,
            'clip.recent_actions': breadcrumbs,
            ...pageContextAttrs(),
          },
          statusCode: 2,
          statusMessage: err?.message || 'fetch failed',
        });
      },
    );
    return promise;
  }) as typeof fetch & PatchableFunction;

  wrapped[PATCH_MARKER] = true;
  globalThis.fetch = wrapped;
}

function patchXhr(ctx: PropagationContext): void {
  if (typeof XMLHttpRequest === 'undefined') {
    return;
  }

  const proto = XMLHttpRequest.prototype as XMLHttpRequest & {
    open: XMLHttpRequest['open'] & PatchableFunction;
    send: XMLHttpRequest['send'] & PatchableFunction;
  };

  if (proto.open[PATCH_MARKER] && proto.send[PATCH_MARKER]) {
    return;
  }

  const originalOpen = proto.open;
  const originalSend = proto.send;
  const STATE = Symbol.for('clipsymphony.trace.xhrState');

  interface XhrState {
    url: string;
    method: string;
    traceId: string;
    spanId: string;
    startTimeMs: number;
    breadcrumbs: string;
  }

  type Tracked = XMLHttpRequest & { [STATE]?: XhrState };

  const newOpen: typeof originalOpen & PatchableFunction = function (
    this: Tracked,
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    const urlStr = typeof url === 'string' ? url : url.toString();
    this[STATE] = {
      url: urlStr,
      method: method.toUpperCase(),
      traceId: '',
      spanId: '',
      startTimeMs: 0,
      breadcrumbs: '',
    };
    return originalOpen.call(
      this,
      method,
      url as string,
      async as boolean,
      username,
      password,
    );
  } as typeof originalOpen & PatchableFunction;
  newOpen[PATCH_MARKER] = true;

  const newSend: typeof originalSend & PatchableFunction = function (
    this: Tracked,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const state = this[STATE];
    if (state && shouldPropagateTo(state.url, ctx.propagationTargets)) {
      try {
        const tp = newTraceparent();
        this.setRequestHeader('traceparent', tp.header);
        state.traceId = tp.traceId;
        state.spanId = tp.spanId;
        state.startTimeMs = Date.now();
        state.breadcrumbs = encodeBreadcrumbs(snapshotBreadcrumbs());
        for (const [k, v] of Object.entries(pageContextHeaders(state.url))) {
          try {
            this.setRequestHeader(k, v);
          } catch {
            /* header already sent — skip */
          }
        }
      } catch {
        // setRequestHeader throws if called in the wrong state. Drop the
        // trace headers silently — losing one trace must not break the
        // request.
      }

      this.addEventListener('loadend', () => {
        if (!state.traceId) return;
        const status = this.status;
        recordBrowserSpan({
          traceId: state.traceId,
          spanId: state.spanId,
          name: `${state.method} ${pathnameOf(state.url)}`,
          kind: SPAN_KIND_CLIENT,
          startTimeMs: state.startTimeMs,
          endTimeMs: Date.now(),
          attributes: {
            'http.method': state.method,
            'http.url': state.url,
            'http.status_code': status,
            'clip.recent_actions': state.breadcrumbs,
            ...pageContextAttrs(),
          },
          statusCode: status >= 200 && status < 400 ? 1 : 2,
          statusMessage: status === 0 ? 'network error' : undefined,
        });
      });
    }
    return originalSend.call(this, body as XMLHttpRequestBodyInit);
  } as typeof originalSend & PatchableFunction;
  newSend[PATCH_MARKER] = true;

  proto.open = newOpen;
  proto.send = newSend;
}
